import { useEffect, useMemo, useState } from 'react';

import { HISTORY_CODES } from 'dex-app.cm';
import { isEqual } from 'lodash/fp';

import { Currency } from '@/constants';
import { useHistoryTracker } from '@/hooks';
import useExplorerByCurrency from '@/hooks/useExplorerByCurrency';
import { NETWORK as BTC_NETWORK } from '@/networks/btc-like/btc';
import { NetworkNet } from '@/networks/btc-like/common';
import { NETWORK as LTC_NETWORK } from '@/networks/btc-like/ltc';
import { Tracker } from '@/types';
import { isCwebCurrency } from '@/utils';

import { useTrackersStore } from '../../../../shared';

const bnbScan = '/tx/0x';
const ethScan = '/tx/0x';
const polygonScan = '/tx/0x';
const arbScan = '/tx/0x';
const baseScan = '/tx/0x';
const btcScan = '/tx/';
const ltcScan = '/tx/';
const ltcTestNetScan = '/testnet/tx/';
const btcTestNetScan = '/testnet/tx/';
const tronScan = '/#/transaction/';
const dogeScan = '/tx/';
const cwebScan = '/tx/';
const solScan = '/tx/';

export function useScanUrlByCurrency(currency: Currency) {
  const explorerLink = useExplorerByCurrency(currency);

  return useMemo(() => {
    switch (currency) {
      case Currency.CWEB:
        return explorerLink.concat(cwebScan);
      case Currency.BNB:
      case Currency.USDT_BNB:
      case Currency.USDC_BNB:
      case Currency.USD1_BNB:
      case Currency.WBTC_BNB:
        return explorerLink.concat(bnbScan);
      case Currency.ETH:
      case Currency.USDT_ETH:
      case Currency.USDC_ETH:
      case Currency.USD1_ETH:
      case Currency.WBTC_ETH:
      case Currency.WCWEB_ETH:
        return explorerLink.concat(ethScan);
      case Currency.LTC:
        return explorerLink.concat(LTC_NETWORK.Network === NetworkNet.Mainnet ? ltcScan : ltcTestNetScan);
      case Currency.BTC:
        return explorerLink.concat(BTC_NETWORK.Network === NetworkNet.Mainnet ? btcScan : btcTestNetScan);
      case Currency.TRX:
      case Currency.USDT_TRX:
        return explorerLink.concat(tronScan);
      case Currency.DOGE:
        return explorerLink.concat(dogeScan);
      case Currency.POL:
      case Currency.USDT_POL:
      case Currency.USDC_POL:
        return explorerLink.concat(polygonScan);
      case Currency.ARB:
      case Currency.USDT_ARB:
      case Currency.USDC_ARB:
      case Currency.ETH_ARB:
        return explorerLink.concat(arbScan);
      case Currency.ETH_BASE:
      case Currency.USDT_BASE:
      case Currency.USDC_BASE:
        return explorerLink.concat(baseScan);
      case Currency.SOL:
      case Currency.USDT_SOL:
      case Currency.USDC_SOL:
        return explorerLink.concat(solScan);
      default:
        return;
    }
  }, [currency]);
}

type TrackerState = {
  outgoingTxId: string;
  incomingL1Tx: string;
  isPactCreated: boolean;
  isReverseCreated: boolean;
  isSwapFinalised: boolean;
  endDate: number;
  startDate: number;
  isFinalised: boolean;
  isReverseFinalized: boolean;
  reverseL1Tx: string;
};

type TrackerName = [Currency, string, string, Currency, string];

function deriveTrackerStateRegularSwap(tracker: Tracker) {
  const { history = [] } = tracker;
  const pactCreatedNotification = history.find((n) => n.code === HISTORY_CODES.C2_REQUEST_CREATED);
  const reverseCreatedNotification = history.find((n) => n.code === HISTORY_CODES.C2_REFUND_REQUEST_CREATED);
  const incomingL1TxNotification = history.find((n) => n.code === HISTORY_CODES.C2_L1_TX_HANDLED);
  const finalisedNotification = history.find((n) => n.code === HISTORY_CODES.C2_REQUEST_EXECUTED);
  const reverseFinalizedNotification = history.find((n) => n.code === HISTORY_CODES.C2_REFUND_REQUEST_EXECUTED);
  const fallbackNotification = history.find((n) => n.code === HISTORY_CODES.C2_REQUEST_FALLBACK_CALL);

  const incomingL1Tx = !fallbackNotification ? (incomingL1TxNotification?.l1TxId ?? '') : '';
  const isPactCreated = Boolean(pactCreatedNotification);
  const isReverseCreated = Boolean(reverseCreatedNotification);
  const isSwapFinalised = Boolean(finalisedNotification);
  const startDate = tracker.time ?? Date.now();
  const endDate = finalisedNotification?.timestamp ?? reverseFinalizedNotification?.timestamp ?? 0;
  const isFinalised = Boolean(finalisedNotification);
  const isReverseFinalized = Boolean(reverseFinalizedNotification);
  const reverseL1Tx = fallbackNotification ? (incomingL1TxNotification?.l1TxId ?? '') : '';

  return {
    outgoingTxId: tracker.unique,
    incomingL1Tx,
    isPactCreated,
    isReverseCreated,
    isSwapFinalised,
    startDate,
    endDate,
    isFinalised,
    isReverseFinalized,
    reverseL1Tx,
  };
}

function deriveTrackerStateFromNativeCwebSwap(tracker: Tracker) {
  const { history = [] } = tracker;
  const pactCreatedNotification = history.find((n) => n.code === HISTORY_CODES.C2_REQUEST_CREATED);
  const incomingCwebL2TxNotification = history.find((n) => n.code === HISTORY_CODES.C2_L1_TX_HANDLED);
  const finalisedNotification = history.find((n) => n.code === HISTORY_CODES.C2_REQUEST_EXECUTED);
  const refundedNotification = history.find((n) => n.code === HISTORY_CODES.C2_FALLBACK_REFUND_CWEB);

  return {
    outgoingTxId: tracker.txId as string,
    incomingL1Tx: incomingCwebL2TxNotification?.txId || '',
    isPactCreated: Boolean(pactCreatedNotification),
    isReverseCreated: Boolean(refundedNotification),
    isSwapFinalised: Boolean(finalisedNotification),
    startDate: tracker.time ?? Date.now(),
    endDate: finalisedNotification?.timestamp ?? 0,
    isFinalised: Boolean(finalisedNotification),
    isReverseFinalized: Boolean(refundedNotification),
    reverseL1Tx: '',
  };
}

function deriveTrackerStateToNativeCwebSwap(tracker: Tracker) {
  const { history = [] } = tracker;
  const incomingCwebL2TxNotification = history.find((n) => n.code === HISTORY_CODES.C1_L1_TAKE_TX_HANDLED);

  return {
    outgoingTxId: tracker.unique,
    incomingL1Tx: incomingCwebL2TxNotification?.txId || '',
    isPactCreated: false,
    isReverseCreated: false,
    isSwapFinalised: Boolean(incomingCwebL2TxNotification?.txId),
    startDate: tracker.time ?? Date.now(),
    endDate: incomingCwebL2TxNotification?.timestamp ?? 0,
    isFinalised: Boolean(incomingCwebL2TxNotification?.txId),
    isReverseFinalized: false,
    reverseL1Tx: '',
  };
}

function deriveTrackerState(tracker: Tracker) {
  const [outgoingCurrency, , , incomingCurrency] = (tracker.name?.split(' ') || []) as TrackerName;

  if (isCwebCurrency(outgoingCurrency)) {
    return deriveTrackerStateFromNativeCwebSwap(tracker);
  }

  if (isCwebCurrency(incomingCurrency)) {
    return deriveTrackerStateToNativeCwebSwap(tracker);
  }

  return deriveTrackerStateRegularSwap(tracker);
}

export function useTrackerNotifications(tracker: Tracker) {
  const { updateTracker } = useTrackersStore();
  const [trackerState, setTrackerState] = useState<TrackerState>(deriveTrackerState(tracker));

  const shouldSkipHistoryFetching = trackerState.isFinalised || trackerState.isReverseFinalized;

  const { data: history, isFetching } = useHistoryTracker(tracker, { skip: shouldSkipHistoryFetching });

  const [outgoingCurrency, , , incomingCurrency] = (tracker.name?.split(' ') || []) as TrackerName;

  useEffect(() => {
    if (!isFetching && history?.length && !isEqual(history, tracker.history)) {
      const updatedTracker = { ...tracker, history };
      // For all legacy trackers, this will add history to the trackers in localStorage
      updateTracker(updatedTracker);
      setTrackerState(deriveTrackerState(updatedTracker));
    }
  }, [isFetching]);

  return {
    ...trackerState,
    outgoingCurrency,
    incomingCurrency,
  };
}

// Re-export BTC hooks from TrackerStateBtc for use by LTC and DOGE
export {
  useBtcLikeLastBlock,
  useBtcLikeTxInfo,
  useEffectiveBtcLikeTxFee,
  useLatestCpfpChildTxId,
  useMempoolSatPerVbyte,
  useTimeElapsedSinceLastBtcLikeBlock,
} from './TrackerStateBtcLike/hooks';
